import { makeStyles } from '@material-ui/core'
import Add from '@material-ui/icons/Add'
import PencilIcon from '@material-ui/icons/Edit'
import WarningOutlined from '@material-ui/icons/WarningOutlined'
import { ScheduleAlert } from 'elements/scheduleDialog/summary/ScheduleSummary'
import validateSchedule from 'elements/scheduleDialog/summary/validateSchedule'
import { DateRange, ScheduleType } from 'elements/scheduleDialog/types'
import { defaultSchedule, groupSchedules } from 'elements/scheduleDialog/utils'
import { Button, IconButton, Tooltip } from 'opensolar-ui'
import { FC, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import { Theme } from 'types/themes'
import StackedSchedule from './StackedSchedule'
import { useConflictedSlots } from '../../elements/scheduleDialog/hooks'

type PropsType = {
  itemList: any[]
  itemIndex: number
  value?: ScheduleType
  onEdit?: () => void
  shortDisplay?: boolean
  disabled?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  alertIcon: {
    color: theme.alertIcon_warning,
    marginRight: 10,
  },
  iconButton: {
    color: 'rgb(77, 77, 77)',
    '&:hover': {
      background: 'rgba(77, 77, 77,0.2)',
    },
    marginLeft: 10,
  },
  emptyMargin: {
    margin: 0,
  },
  stackedWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minWidth: 0,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    margin: '5px 0',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  shortRow: {
    display: 'flex',
  },
}))

const Schedule: FC<PropsType> = ({ itemList, itemIndex, onEdit, disabled, shortDisplay = false }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const allSchedules = itemList?.map((item: any) => item.schedule) || []
  const value: ScheduleType = allSchedules[itemIndex] ? allSchedules[itemIndex] : defaultSchedule()
  const groupedSchedules = useMemo(() => groupSchedules(value?.applicable_days_and_hours?.data), [
    value?.applicable_days_and_hours?.data,
  ])
  const schedulesLength = groupedSchedules && Object.keys(groupedSchedules).length
  const noDateRanges = !!value?.date_ranges?.filter((x: DateRange) => x?.start_date && x?.end_date).length
  const unavailableSlots = useConflictedSlots(value, allSchedules, itemIndex)
  const scheduleErrors = useMemo(() => {
    return validateSchedule(value, unavailableSlots)
  }, [value, unavailableSlots])

  return (
    <>
      {!!!schedulesLength && !noDateRanges ? (
        <>
          {shortDisplay ? (
            <></>
          ) : (
            <Button
              disabled={disabled}
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                if (onEdit) onEdit()
              }}
              className={classes.emptyMargin}
            >
              {translate('Set Time-of-Use Schedule')}
            </Button>
          )}
        </>
      ) : (
        <div className={classes.row}>
          {!!scheduleErrors.length && !shortDisplay && (
            <Tooltip title={<ScheduleAlert errors={scheduleErrors} />}>
              <WarningOutlined className={classes.alertIcon} />
            </Tooltip>
          )}
          <div className={classes.headerRow}>
            <div className={`${classes.stackedWrapper} columnWrapper`}>
              <StackedSchedule value={value} groupedSchedules={groupedSchedules} schedulesLength={schedulesLength} />
            </div>
            {!shortDisplay && (
              <IconButton size="small" classes={{ root: classes.iconButton }} onClick={onEdit} disabled={disabled}>
                <PencilIcon />
              </IconButton>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default Schedule
